import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Title, Paper, Stack, Group, Text, Badge, Loader, Divider,
  TextInput, Center, Menu, ActionIcon, MultiSelect, NumberInput, Modal, PasswordInput
} from '@mantine/core';
import {
  IconArrowLeft, IconEdit, IconDots, IconUserCheck, IconLock, IconTrash,
  IconBrandTelegram, IconRefresh, IconKey, IconCheck, IconX, IconLogout
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { usersApi, groupsApi, telegramApi } from '../api/client';
import { User } from '../types/user';
import { Group as GroupType } from '../types/group';
import { useAuthStore } from '../store/authSlice';
import { PageContainer } from './PageContainer';
import { formatDateTime } from '../utils/date';
import { UserProfileEditor } from './UserProfileEditor';

interface UserProfileProps {
  userId?: string;
  isOwnProfile?: boolean;
  showBackButton?: boolean;
  showAdminControls?: boolean;
}

const DEFAULT_GROUP_ID = '00000000-0000-0000-0000-000000000001';

export function UserProfile({ userId, isOwnProfile = false, showBackButton = true, showAdminControls = false }: UserProfileProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser, updateUser, logout } = useAuthStore();

  const [user, setUser] = useState<User | null>(null);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [userGroupIds, setUserGroupIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    telegram_id: null as number | null, telegram_username: ''
  });
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [fetchingTgUser, setFetchingTgUser] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const effectiveUserId = userId || authUser?.id;

  useEffect(() => {
    if (!effectiveUserId) return;

    const loadData = async () => {
      try {
        const promises: Promise<any>[] = [usersApi.getById(effectiveUserId)];
        if (showAdminControls) {
          promises.push(groupsApi.list(), usersApi.getGroups(effectiveUserId));
        }

        const results = await Promise.all(promises);
        const userData = results[0].data?.data;
        setUser(userData);
        setForm({
          first_name: userData?.first_name || '',
          last_name: userData?.last_name || '',
          email: userData?.email || '',
          phone: userData?.phone || '',
          telegram_id: userData?.telegram_id || null,
          telegram_username: userData?.telegram_username || ''
        });

        if (showAdminControls && results.length > 1) {
          setGroups(results[1].data?.data || []);
          setUserGroupIds(results[2].data?.data?.groups || []);
        }
      } catch (err) {
        console.error('Failed to load user:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [effectiveUserId, showAdminControls]);

  const handleSave = async () => {
    if (!effectiveUserId) return;
    setSaving(true);
    try {
      const updateData: Record<string, unknown> = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
      };
      if (form.telegram_id) {
        updateData.telegram_id = form.telegram_id;
        updateData.telegram_username = form.telegram_username;
      }
      await usersApi.update(effectiveUserId, updateData);
      const res = await usersApi.getById(effectiveUserId);
      const updatedUser = res.data?.data;
      setUser(updatedUser);
      if (isOwnProfile) updateUser(updatedUser);
      setEditMode(false);
      notifications.show({ title: t('common.success'), message: t('users.updated_success'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.update_failed'), color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleFetchTelegramUser = async () => {
    if (!form.telegram_id) return;
    setFetchingTgUser(true);
    try {
      const res = await telegramApi.getUser(form.telegram_id);
      const tgUser = res.data?.data;
      if (tgUser) {
        setForm(prev => ({
          ...prev,
          telegram_username: tgUser.username || '',
          first_name: prev.first_name || tgUser.first_name || '',
          last_name: prev.last_name || tgUser.last_name || ''
        }));
        notifications.show({ title: t('common.success'), message: t('users.telegram_fetched'), color: 'green' });
      }
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.telegram_fetch_failed'), color: 'red' });
    } finally {
      setFetchingTgUser(false);
    }
  };

  const handleRefreshTelegramData = async () => {
    if (!user?.telegram_id) return;
    setFetchingTgUser(true);
    try {
      const res = await telegramApi.getUser(user.telegram_id);
      const tgUser = res.data?.data;
      if (tgUser && effectiveUserId) {
        const updateData: Record<string, unknown> = {};
        if (tgUser.username) updateData.telegram_username = tgUser.username;
        if (tgUser.first_name && !user.first_name) updateData.first_name = tgUser.first_name;
        if (tgUser.last_name && !user.last_name) updateData.last_name = tgUser.last_name;
        
        if (Object.keys(updateData).length > 0) {
          await usersApi.update(effectiveUserId, updateData);
          const refreshRes = await usersApi.getById(effectiveUserId);
          const updatedUser = refreshRes.data?.data;
          setUser(updatedUser);
          setForm(prev => ({
            ...prev,
            telegram_username: updatedUser?.telegram_username || prev.telegram_username,
            first_name: updatedUser?.first_name || prev.first_name,
            last_name: updatedUser?.last_name || prev.last_name
          }));
          if (isOwnProfile) updateUser(updatedUser);
        }
        notifications.show({ title: t('common.success'), message: t('users.telegram_fetched'), color: 'green' });
      }
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.telegram_fetch_failed'), color: 'red' });
    } finally {
      setFetchingTgUser(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!effectiveUserId) return;
    setStatusUpdating(true);
    try {
      await usersApi.updateStatus(effectiveUserId, newStatus);
      const res = await usersApi.getById(effectiveUserId);
      setUser(res.data?.data);
      notifications.show({ title: t('common.success'), message: t('users.status_updated'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.status_update_failed'), color: 'red' });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!effectiveUserId) return;
    if (!confirm(t('users.delete_confirm', { login: user?.login }))) return;
    try {
      await usersApi.delete(effectiveUserId);
      notifications.show({ title: t('common.success'), message: t('users.deleted_success'), color: 'green' });
      navigate('/users');
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.delete_failed'), color: 'red' });
    }
  };

  const handleGroupsChange = async (newGroupIds: string[]) => {
    if (!effectiveUserId) return;
    if (!newGroupIds.includes(DEFAULT_GROUP_ID) && userGroupIds.includes(DEFAULT_GROUP_ID)) {
      newGroupIds = [DEFAULT_GROUP_ID, ...newGroupIds];
    }
    const toAdd = newGroupIds.filter(gid => !userGroupIds.includes(gid));
    const toRemove = userGroupIds.filter(gid => !newGroupIds.includes(gid) && gid !== DEFAULT_GROUP_ID);
    try {
      for (const gid of toAdd) await groupsApi.addUser(gid, effectiveUserId);
      for (const gid of toRemove) await groupsApi.removeUser(gid, effectiveUserId);
      setUserGroupIds(newGroupIds);
      notifications.show({ title: t('common.success'), message: t('users.groups_updated'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.groups_update_failed'), color: 'red' });
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      notifications.show({ title: t('common.error'), message: t('profile.passwords_not_match'), color: 'red' });
      return;
    }
    if (passwordForm.new_password.length < 6) {
      notifications.show({ title: t('common.error'), message: t('profile.password_too_short'), color: 'red' });
      return;
    }
    if (!effectiveUserId) return;
    setPasswordLoading(true);
    try {
      await usersApi.changePassword(effectiveUserId, passwordForm.current_password || null, passwordForm.new_password);
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      notifications.show({ title: t('common.success'), message: t('profile.password_changed'), color: 'green' });
    } catch (err: any) {
      notifications.show({
        title: t('common.error'),
        message: err.response?.data?.error?.message || t('profile.password_change_failed'),
        color: 'red'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'pending': return 'yellow';
      case 'blocked': return 'red';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return t('users.status_active');
      case 'pending': return t('users.status_pending');
      case 'blocked': return t('users.status_blocked');
      default: return status;
    }
  };

  if (loading) {
    return <Center h="50vh"><Loader size="lg" /></Center>;
  }

  if (!user) {
    return (
      <PageContainer width="detail">
        <Text ta="center" c="dimmed">{t('users.not_found')}</Text>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="detail">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            {showBackButton && (
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate(-1)} title={t('common.back')}>
                <IconArrowLeft size={20} />
              </ActionIcon>
            )}
            <Title order={2}>{isOwnProfile ? t('profile.my_profile') : user.login}</Title>
          </Group>
          <Group gap="xs">
            {editMode ? (
              <>
                <ActionIcon variant="light" color="gray" size="lg" onClick={() => setEditMode(false)} title={t('common.cancel')}>
                  <IconX size={18} />
                </ActionIcon>
                <ActionIcon variant="filled" color="green" size="lg" onClick={handleSave} loading={saving} title={t('common.save')}>
                  <IconCheck size={18} />
                </ActionIcon>
              </>
            ) : (
              <>
                <ActionIcon variant="light" size="lg" onClick={() => setEditMode(true)} title={t('common.edit')}>
                  <IconEdit size={18} />
                </ActionIcon>
                {showAdminControls && (
                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="light" size="lg" loading={statusUpdating}>
                        <IconDots size={18} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>{t('users.status')}</Menu.Label>
                      <Menu.Item leftSection={<IconUserCheck size={14} />} onClick={() => handleStatusChange('active')} disabled={user.status === 'active'}>
                        {t('users.activate')}
                      </Menu.Item>
                      <Menu.Item leftSection={<IconLock size={14} />} onClick={() => handleStatusChange('blocked')} disabled={user.status === 'blocked'}>
                        {t('users.block')}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={handleDelete}>
                        {t('common.delete')}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </>
            )}
          </Group>
        </Group>

        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Status with refresh button */}
            <Group justify="space-between">
              <Badge color={getStatusColor(user.status)}>{getStatusLabel(user.status)}</Badge>
              {user.telegram_id && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleRefreshTelegramData}
                  loading={fetchingTgUser}
                  title={t('users.refresh_telegram')}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              )}
            </Group>

            <Divider />

            {/* Basic info */}
            <TextInput
              label={t('users.first_name')}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              disabled={!editMode}
            />

            <TextInput
              label={t('users.last_name')}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              disabled={!editMode}
            />

            <TextInput
              label={t('users.email')}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={!editMode}
            />

            <TextInput
              label={t('users.phone')}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled={!editMode}
            />

            {/* Telegram section */}
            <Divider label={<Group gap="xs"><IconBrandTelegram size={16} /><Text style={{ fontSize: 'var(--font-size-sm)' }}>{t('common.telegram')}</Text></Group>} />

            <Group grow align="flex-end">
              <NumberInput
                label={t('users.telegram_id')}
                value={form.telegram_id || ''}
                onChange={(val) => setForm({ ...form, telegram_id: typeof val === 'number' ? val : null })}
                disabled={!editMode}
                hideControls
                placeholder="123456789"
              />
              {editMode && (
                <ActionIcon
                  variant="light"
                  color="blue"
                  size="lg"
                  onClick={handleFetchTelegramUser}
                  loading={fetchingTgUser}
                  disabled={!form.telegram_id}
                  title={t('users.fetch_telegram')}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              )}
            </Group>

            <TextInput
              label={t('users.telegram_username')}
              value={form.telegram_username}
              onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
              disabled={!editMode}
              placeholder="username"
              leftSection={<Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>@</Text>}
            />

            {/* Groups (admin only) */}
            {showAdminControls && (
              <>
                <Divider />
                <MultiSelect
                  label={t('users.groups')}
                  placeholder={t('users.select_groups')}
                  data={groups.map(g => ({
                    value: g.id,
                    label: g.name,
                    disabled: g.id === DEFAULT_GROUP_ID
                  }))}
                  value={userGroupIds}
                  onChange={handleGroupsChange}
                  searchable
                />
              </>
            )}

            {/* User Profiles */}
            {showAdminControls && effectiveUserId && (
              <>
                <Divider />
                <UserProfileEditor userId={effectiveUserId} />
              </>
            )}

            {/* Password change */}
            <Divider />
            <Group justify="flex-end">
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                onClick={() => setShowPasswordModal(true)}
                title={t('profile.change_password')}
              >
                <IconKey size={18} />
              </ActionIcon>
            </Group>

            {/* Meta info */}
            <Divider />
            <Group gap="xs">
              <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{t('users.created')}:</Text>
              <Text style={{ fontSize: 'var(--font-size-sm)' }}>{formatDateTime(user.created_at)}</Text>
            </Group>

            {/* Logout for own profile */}
            {isOwnProfile && (
              <>
                <Divider />
                <Group justify="flex-end">
                  <ActionIcon variant="light" color="red" size="lg" onClick={logout} title={t('auth.logout')}>
                    <IconLogout size={18} />
                  </ActionIcon>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Password Modal */}
      <Modal opened={showPasswordModal} onClose={() => setShowPasswordModal(false)} title={t('profile.change_password')}>
        <Stack>
          <PasswordInput
            label={t('profile.current_password')}
            value={passwordForm.current_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
          />
          <PasswordInput
            label={t('profile.new_password')}
            value={passwordForm.new_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
          />
          <PasswordInput
            label={t('profile.confirm_password')}
            value={passwordForm.confirm_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
          />
          <Group justify="flex-end">
            <ActionIcon variant="light" color="gray" size="lg" onClick={() => setShowPasswordModal(false)} title={t('common.cancel')}>
              <IconX size={18} />
            </ActionIcon>
            <ActionIcon variant="filled" color="green" size="lg" onClick={handleChangePassword} loading={passwordLoading} title={t('common.save')}>
              <IconCheck size={18} />
            </ActionIcon>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
}

export default UserProfile;
